import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface PageImage {
  id: string;
  image_url: string;
  pair_image_url: string | null;
  caption: string | null;
  layout_type: string;
  sort_order: number;
}

interface VoteState {
  [imageId: string]: boolean | null;
}

export default function ClientApprovalPage() {
  const { slug } = useParams<{ slug: string }>();
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pageName, setPageName] = useState('');
  const [pageId, setPageId] = useState('');
  const [images, setImages] = useState<PageImage[]>([]);
  const [votes, setVotes] = useState<VoteState>({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [sent, setSent] = useState(false);

  // Check if page exists (no sensitive data returned)
  useEffect(() => {
    if (!slug) return;

    supabase
      .rpc('get_client_page_info', { p_slug: slug })
      .then(({ data, error: err }) => {
        if (err || !data || data.length === 0) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const page = data[0];
        setPageName(page.name);
        setLoading(false);

        // Try stored password to auto-login
        const storedPwd = sessionStorage.getItem(`approval-pwd-${slug}`);
        if (storedPwd) {
          fetchApprovalData(slug, storedPwd);
        }
      });
  }, [slug]);

  const fetchApprovalData = async (pageSlug: string, pwd: string) => {
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/get-approval-images`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug: pageSlug, password: pwd }),
        }
      );

      if (!res.ok) {
        sessionStorage.removeItem(`approval-pwd-${pageSlug}`);
        return false;
      }

      const data = await res.json();
      setPageId(data.page_id);
      setPageName(data.page_name);
      setImages(data.images || []);
      setAuthed(true);

      // Load local vote state
      const storedVotes = localStorage.getItem(`votes-${data.page_id}`);
      if (storedVotes) setVotes(JSON.parse(storedVotes));

      const storedSent = sessionStorage.getItem(`approval-sent-${data.page_id}`);
      setSent(storedSent === '1');

      return true;
    } catch {
      return false;
    }
  };

  const handleLogin = async () => {
    if (!slug) return;

    const success = await fetchApprovalData(slug, password);
    if (success) {
      sessionStorage.setItem(`approval-pwd-${slug}`, password);
      setError('');
    } else {
      setError('Senha incorreta');
    }
  };

  const callVoteApi = async (image_id: string, action: string, approved?: boolean) => {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const storedPwd = sessionStorage.getItem(`approval-pwd-${slug}`);
    await fetch(`https://${projectId}.supabase.co/functions/v1/submit-vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, password: storedPwd, image_id, action, approved }),
    });
  };

  const handleVote = async (imageId: string, approved: boolean) => {
    const currentVote = votes[imageId];

    if (currentVote === approved) {
      await callVoteApi(imageId, 'delete');
      const newVotes = { ...votes };
      delete newVotes[imageId];
      setVotes(newVotes);
      localStorage.setItem(`votes-${pageId}`, JSON.stringify(newVotes));
      setSent(false);
      sessionStorage.removeItem(`approval-sent-${pageId}`);
      return;
    }

    await callVoteApi(imageId, 'vote', approved);
    const newVotes = { ...votes, [imageId]: approved };
    setVotes(newVotes);
    localStorage.setItem(`votes-${pageId}`, JSON.stringify(newVotes));
    setSent(false);
    sessionStorage.removeItem(`approval-sent-${pageId}`);
  };

  const handleClearAll = async () => {
    if (!pageId) return;

    const voteKeys = Object.keys(votes);
    if (!voteKeys.length) return;

    if (!window.confirm('Limpar todas as seleções feitas até agora?')) return;

    // Delete each vote through the edge function
    const imageIds = images.map((img) => img.id);
    await Promise.all(imageIds.map((id) => callVoteApi(id, 'delete')));

    setVotes({});
    setSent(false);
    localStorage.removeItem(`votes-${pageId}`);
    sessionStorage.removeItem(`approval-sent-${pageId}`);
  };

  const handleSubmitSelections = () => {
    if (!Object.keys(votes).length) return;
    setSent(true);
    sessionStorage.setItem(`approval-sent-${pageId}`, '1');
  };

  const approvedCount = Object.values(votes).filter((v) => v === true).length;
  const rejectedCount = Object.values(votes).filter((v) => v === false).length;
  const hasSelections = approvedCount + rejectedCount > 0;

  const monoFont = "'IBM Plex Mono', monospace";

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <p className="text-xs uppercase opacity-40" style={{ fontFamily: monoFont }}>
          Carregando...
        </p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <p className="text-sm uppercase opacity-60" style={{ fontFamily: monoFont }}>
          Página não encontrada
        </p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 p-8 max-w-xs w-full">
          <h1 className="uppercase text-sm font-light tracking-wider text-center" style={{ fontFamily: monoFont }}>
            {pageName}
          </h1>
          <p className="text-xs opacity-50 text-center" style={{ fontFamily: monoFont }}>
            Digite a senha para acessar
          </p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            className="w-full text-center text-sm bg-transparent"
            style={{
              fontFamily: monoFont,
              padding: '10px',
              border: '1px solid hsl(0 0% 0% / 0.2)',
            }}
            placeholder="•••••"
            autoFocus
          />
          {error && <p className="text-xs text-destructive" style={{ fontFamily: monoFont }}>{error}</p>}
          <button
            onClick={handleLogin}
            className="uppercase text-xs py-2 px-6 nav-hover-bg"
            style={{ border: '1px solid hsl(0 0% 0%)', fontFamily: monoFont }}
          >
            ENTRAR
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto bg-background">
      <div className="sticky top-0 z-10 bg-background py-4 px-4 text-center" style={{ borderBottom: '1px solid hsl(0 0% 0% / 0.08)' }}>
        <h1 className="uppercase text-sm font-light tracking-wider" style={{ fontFamily: monoFont }}>
          {pageName}
        </h1>
      </div>

      <div className="max-w-4xl mx-auto p-2 pb-28 flex flex-col gap-2">
        {images.map((img) => (
          <div key={img.id}>
            {img.layout_type === 'pair' && img.pair_image_url ? (
              <div className="flex gap-2">
                <img src={img.image_url} alt={img.caption || ''} className="w-1/2 object-cover" loading="lazy" />
                <img src={img.pair_image_url} alt={img.caption || ''} className="w-1/2 object-cover" loading="lazy" />
              </div>
            ) : (
              <img src={img.image_url} alt={img.caption || ''} className="w-full object-cover" loading="lazy" />
            )}

            <div className="flex items-center justify-between py-2 px-1 gap-3">
              <span className="text-xs opacity-50" style={{ fontFamily: monoFont }}>
                {img.caption || ''}
              </span>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => handleVote(img.id, true)}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 transition-all"
                  style={{
                    fontFamily: monoFont,
                    border: votes[img.id] === true ? '2px solid hsl(142 71% 45%)' : '1px solid hsl(0 0% 0% / 0.15)',
                    color: votes[img.id] === true ? 'hsl(142 71% 45%)' : 'inherit',
                    backgroundColor: votes[img.id] === true ? 'hsl(142 71% 45% / 0.06)' : 'transparent',
                  }}
                >
                  ✓ APROVAR
                </button>
                <button
                  onClick={() => handleVote(img.id, false)}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 transition-all"
                  style={{
                    fontFamily: monoFont,
                    border: votes[img.id] === false ? '2px solid hsl(0 84% 60%)' : '1px solid hsl(0 0% 0% / 0.15)',
                    color: votes[img.id] === false ? 'hsl(0 84% 60%)' : 'inherit',
                    backgroundColor: votes[img.id] === false ? 'hsl(0 84% 60% / 0.06)' : 'transparent',
                  }}
                >
                  ✕ REPROVAR
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 z-10 bg-background/95 backdrop-blur px-3 py-3" style={{ borderTop: '1px solid hsl(0 0% 0% / 0.1)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase opacity-60" style={{ fontFamily: monoFont }}>
            {approvedCount} aprovadas • {rejectedCount} reprovadas {sent ? '• enviado' : ''}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleClearAll}
              disabled={!hasSelections}
              className="uppercase text-[10px] py-2 px-3 border border-destructive text-destructive disabled:opacity-30"
              style={{ fontFamily: monoFont }}
            >
              Limpar tudo
            </button>
            <button
              onClick={handleSubmitSelections}
              disabled={!hasSelections}
              className="uppercase text-[10px] py-2 px-4 border border-foreground bg-primary text-primary-foreground disabled:opacity-30"
              style={{ fontFamily: monoFont }}
            >
              {sent ? 'Enviado' : 'Enviar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
